/**
 * EditPaymentDialog — «Редактировать» транш: плановая дата + плановая сумма + комментарий одним окном.
 * Собирает патч из изменённых полей и шлёт PATCH с If-Match: version.
 */

import { useState } from 'react';
import type { PaymentDto, UpdatePaymentRequest } from '../../../lib/types';
import { formatRub, minorToRubString, rubToMinor } from '../../../lib/money';
import { fmtDate } from '../lib/dates';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { DatePicker } from '../ui/DatePicker';
import { useUpdatePayment, type DealScope } from '../api';
import { ApiError } from '../../../lib/apiClient';

export interface EditPaymentDialogProps {
  scope: DealScope;
  payment: PaymentDto;
  onClose: () => void;
}

function minorToEditable(minor: string): string {
  return minorToRubString(minor).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

export function EditPaymentDialog({ scope, payment, onClose }: EditPaymentDialogProps): JSX.Element {
  const [date, setDate] = useState<string>(payment.plannedDate);
  const [dateOpen, setDateOpen] = useState(false);
  const [amountText, setAmountText] = useState<string>(() => minorToEditable(payment.plannedAmountMinor));
  const [amountError, setAmountError] = useState(false);
  const [comment, setComment] = useState<string>(payment.comment ?? '');
  const [error, setError] = useState<string | null>(null);

  const update = useUpdatePayment(scope);

  const buildPatch = (): UpdatePaymentRequest | null => {
    const patch: UpdatePaymentRequest = {};
    if (date !== payment.plannedDate) patch.plannedDate = date;
    let amountMinor: string;
    try {
      amountMinor = rubToMinor(amountText);
    } catch {
      setAmountError(true);
      return null;
    }
    if (BigInt(amountMinor) <= 0n) {
      setAmountError(true);
      return null;
    }
    if (amountMinor !== payment.plannedAmountMinor) patch.plannedAmountMinor = amountMinor;
    const trimmed = comment.trim();
    if (trimmed !== (payment.comment ?? '')) patch.comment = trimmed;
    return patch;
  };

  const submit = (): void => {
    setError(null);
    setAmountError(false);
    const patch = buildPatch();
    if (!patch) return;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    update.mutate(
      { id: payment.id, version: payment.version, patch },
      {
        onSuccess: () => onClose(),
        onError: (e) => {
          if (e instanceof ApiError && e.isVersionConflict) {
            setError('Транш изменён в другом месте — данные обновлены, повторите правку.');
          } else {
            setError(e instanceof ApiError ? e.message : 'Не удалось сохранить изменения.');
          }
        },
      },
    );
  };

  return (
    <Dialog
      title={`Редактирование транша №${payment.number}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Отмена
          </Button>
          <Button variant="primary" onClick={submit} disabled={update.isPending}>
            {update.isPending ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div className="pw-field">
        <span className="pw-field__label">Плановая дата</span>
        <DatePicker
          value={date}
          open={dateOpen}
          onOpenChange={setDateOpen}
          onSelect={(ymd) => {
            if (ymd) setDate(ymd);
          }}
          trigger={
            <button type="button" className="pw-input pw-input--date" aria-label="Плановая дата">
              {fmtDate(date)}
            </button>
          }
        />
      </div>

      <label className="pw-field">
        <span className="pw-field__label">Плановая сумма</span>
        <span className="pw-money pw-field__control">
          <input
            type="text"
            inputMode="decimal"
            className="pw-money__input pw-num"
            value={amountText}
            aria-invalid={amountError}
            aria-label="Плановая сумма в рублях"
            onChange={(e) => {
              const v = e.target.value;
              if (/^[\d\s .,₽]*$/.test(v)) {
                setAmountText(v);
                setAmountError(false);
              }
            }}
          />
          <span className="pw-money__suffix" aria-hidden>
            ₽
          </span>
        </span>
        {amountError ? (
          <span className="pw-field__err" role="alert">
            Введите корректную сумму больше нуля.
          </span>
        ) : (
          <span className="pw-field__hint pw-muted">Текущая: {formatRub(payment.plannedAmountMinor)}</span>
        )}
      </label>

      <label className="pw-field">
        <span className="pw-field__label">Комментарий</span>
        <textarea
          className="pw-textarea"
          rows={2}
          value={comment}
          maxLength={500}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Необязательно"
        />
      </label>

      {error ? (
        <p className="pw-field__err" role="alert">
          {error}
        </p>
      ) : null}
    </Dialog>
  );
}
